import React, { useState, useEffect } from "react";
import partialCancelBooking from "./partialCancelBooking";

const PartialCancelModal = ({
  hotelId,
  bookingId,
  roomDetails,
  currencySymbol,
  cancellationMessage,
  onClose,
  onCancelled,
}) => {
  const [cancelQuantities, setCancelQuantities] = useState([]);
  const [confirmStep, setConfirmStep] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const totalRooms = roomDetails.reduce(
    (sum, room) => sum + parseInt(room.no_of_rooms),
    0
  );

  useEffect(() => {
    const defaults = roomDetails.map((room) =>
      Math.min(1, parseInt(room.no_of_rooms))
    );
    if (defaults.reduce((sum, qty) => sum + qty, 0) >= totalRooms) {
      const lastSelectedIndex = defaults.findIndex((qty) => qty > 0);
      if (lastSelectedIndex !== -1) defaults[lastSelectedIndex] -= 1;
    }
    setCancelQuantities(defaults);
  }, [roomDetails, totalRooms]);

  const totalRoomsToCancel = cancelQuantities.reduce(
    (sum, qty) => sum + qty,
    0
  );

  const getLineCancelAmount = (room, qty) => {
    if (room.room_wise_price && room.room_wise_price.length > 0) {
      return room.room_wise_price
        .slice(0, qty)
        .reduce((sum, rp) => sum + rp.price, 0);
    }
    const avgPrice = parseFloat(room.total_price) / parseInt(room.no_of_rooms);
    return avgPrice * qty;
  };

  const totalCancelAmount = roomDetails.reduce(
    (sum, room, index) =>
      sum + getLineCancelAmount(room, cancelQuantities[index]),
    0
  );

  const setQuantity = (index, value) => {
    setCancelQuantities((prev) => {
      const otherLinesTotal = totalRoomsToCancel - prev[index];
      const maxForLine = Math.min(
        parseInt(roomDetails[index].no_of_rooms),
        totalRooms - otherLinesTotal - 1
      );
      const clamped = Math.max(0, Math.min(maxForLine, value));
      return prev.map((qty, i) => (i === index ? clamped : qty));
    });
  };

  const handleReviewSelection = () => {
    if (totalRoomsToCancel === 0) {
      return;
    }
    if (totalRoomsToCancel >= totalRooms) {
      return;
    }
    setConfirmStep(true);
  };

  const handleConfirm = () => {
    const rooms = roomDetails
      .map((room, index) => ({
        room_type_name: room.room_type_name,
        plan_type: room.plan_type,
        cancel_quantity: cancelQuantities[index],
        cancel_amount: getLineCancelAmount(room, cancelQuantities[index]),
      }))
      .filter((room) => room.cancel_quantity > 0);

    setLoading(true);
    partialCancelBooking(hotelId, bookingId, rooms, setLoading, setResult);
  };

  const handleClose = () => {
    if (result && result.status === 1) {
      onCancelled();
    }
    onClose();
  };

  if (result) {
    return (
      <div className="modal-wrapper">
        <div className="modal scroll" style={{ background: "transparent" }}>
          <div className="modal-content">
            <div className="login-wrapper card z-depth-3">
              <span
                className="modal-close waves-effect btnCloseRoom"
                onClick={handleClose}
              />
              <div className="row mx-auto">
                <div className="col center-align m12">
                  <div>
                    <i
                      className={
                        result.status === 1 ? "success-icon" : "cancel-icon"
                      }
                    />
                  </div>
                  <br />
                  <p>{result.message}</p>
                  <div className="row" />
                  <div className="row" />
                  <div className="row">
                    <div className="col s12 m12">
                      <button
                        type="submit"
                        className="col s12 btn waves-effect apply-close"
                        onClick={handleClose}
                      >
                        OK
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          className="modal-overlay"
          style={{ zIndex: "1230", display: "block", opacity: "0.5" }}
        />
      </div>
    );
  }

  return (
    <div className="modal-wrapper">
      <div className="modal scroll" style={{ background: "transparent" }}>
        <div className="modal-content">
          <div className="login-wrapper card z-depth-3">
            <span
              className="modal-close waves-effect btnCloseRoom"
              onClick={handleClose}
            />

            {confirmStep ? (
              <>
                <h3 className="center-align" style={{ fontSize: "18px" }}>
                  Confirm Cancellation
                </h3>
                <hr />

                <div className="partial-cancel-confirm-box">
                  <p>
                    <i className="fa fa-exclamation-triangle" /> This action
                    cannot be undone.
                  </p>
                </div>

                <p style={{ fontSize: "14px", color: "#555" }}>
                  Are you sure you want to cancel{" "}
                  <strong>
                    {totalRoomsToCancel} room
                    {totalRoomsToCancel > 1 ? "s" : ""}
                  </strong>{" "}
                  for an estimated{" "}
                  <strong style={{ color: "#1976d2" }}>
                    <i className={currencySymbol} /> {totalCancelAmount.toFixed(2)}
                  </strong>
                  ?
                </p>

                <div style={{ marginTop: "12px" }}>
                  {roomDetails.map((room, index) => {
                    const qty = cancelQuantities[index];
                    if (qty === 0) return null;
                    return (
                      <div className="partial-cancel-confirm-item" key={index}>
                        <span>
                          {qty} x {room.room_type_name} ({room.plan_type})
                        </span>
                        <strong>
                          <i className={currencySymbol} />{" "}
                          {getLineCancelAmount(room, qty).toFixed(2)}
                        </strong>
                      </div>
                    );
                  })}
                </div>

                <div className="row" style={{ marginTop: "20px" }}>
                  <div className="col s6">
                    <button
                      type="button"
                      className="col s12 btn waves-effect apply-close"
                      onClick={() => setConfirmStep(false)}
                      disabled={loading}
                    >
                      No, Go Back
                    </button>
                  </div>
                  <div className="col s6">
                    <button
                      type="button"
                      className="col s12 btn waves-effect proceed-cancellation"
                      onClick={handleConfirm}
                      disabled={loading}
                    >
                      Yes, Cancel Rooms{" "}
                      {loading && <span className="payLoader" />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <h3 className="center-align" style={{ fontSize: "18px" }}>
                  Cancel Rooms
                </h3>
                <hr />

                {cancellationMessage && (
                  <div className="cancel-choice-message">
                    <i className="fa fa-info-circle" />
                    <span>{cancellationMessage}</span>
                  </div>
                )}

                <p style={{ fontSize: "13px", color: "#777" }}>
                  Choose how many rooms of each type you'd like to cancel.
                </p>

                {roomDetails.map((room, index) => {
                  const qty = cancelQuantities[index];
                  const isFullyCancelled =
                    qty === parseInt(room.no_of_rooms);
                  const lineAmount = getLineCancelAmount(room, qty);

                  return (
                    <div className="partial-cancel-room-row" key={index}>
                      <div
                        className="room-icon"
                        style={
                          isFullyCancelled
                            ? { background: "#ffebee", color: "#e53935" }
                            : {}
                        }
                      >
                        <i className="fa fa-bed" />
                      </div>

                      <div className="room-info">
                        <h5>
                          {room.room_type_name} ({room.plan_type})
                        </h5>
                        <span>Booked: {room.no_of_rooms}</span>
                      </div>

                      <div className="quantity-controls">
                        <button
                          type="button"
                          className="quantity-btn"
                          onClick={() => setQuantity(index, qty - 1)}
                          disabled={qty <= 0}
                        >
                          -
                        </button>
                        <span className="quantity-value">{qty}</span>
                        <button
                          type="button"
                          className="quantity-btn"
                          onClick={() => setQuantity(index, qty + 1)}
                          disabled={
                            qty >= parseInt(room.no_of_rooms) ||
                            totalRoomsToCancel >= totalRooms - 1
                          }
                        >
                          +
                        </button>
                      </div>

                      {qty > 0 && (
                        <div className="line-amount">
                          <span>
                            Amount for {qty} room{qty > 1 ? "s" : ""}:
                          </span>
                          <strong>
                            <i className={currencySymbol} /> {lineAmount.toFixed(2)}
                          </strong>
                        </div>
                      )}
                    </div>
                  );
                })}

                <div className="partial-cancel-summary">
                  <div className="summary-row">
                    <span>Rooms to cancel</span>
                    <span>
                      {totalRoomsToCancel} of {totalRooms}
                    </span>
                  </div>
                  <div className="summary-row total">
                    <span>Estimated cancellation amount</span>
                    <span>
                      <i className={currencySymbol} /> {totalCancelAmount.toFixed(2)}
                    </span>
                  </div>
                </div>

                {totalRoomsToCancel >= totalRooms - 1 && (
                  <p className="partial-cancel-warning">
                    At least one room must stay booked. To cancel everything,
                    use Full Booking Cancellation instead.
                  </p>
                )}

                <div className="row" style={{ marginTop: "16px" }}>
                  <div className="col s6">
                    <button
                      type="button"
                      className="col s12 btn waves-effect apply-close"
                      onClick={handleClose}
                    >
                      Back
                    </button>
                  </div>
                  <div className="col s6">
                    <button
                      type="button"
                      className="col s12 btn waves-effect proceed-cancellation"
                      onClick={handleReviewSelection}
                      disabled={totalRoomsToCancel === 0}
                    >
                      Confirm Cancellation
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div
        className="modal-overlay"
        style={{ zIndex: "1230", display: "block", opacity: "0.5" }}
      />
    </div>
  );
};

export default PartialCancelModal;
